import { Row, Spin } from "antd";
import { useFetchMessages } from "./hooks/useFetchMessages";
import { EmptyList } from "./components/EmptyList";
import { List } from "./components/List";
import { useRef, useState } from "react";
import { usePollUnreads } from "./hooks/usePollUnreads";
import { IMessage } from "../../types/Message";
import { useOnScrolledToTop } from "./hooks/useOnScrollTop";
import { useLazyLoad } from "./hooks/useLazyLoad";
import { LoadingOutlined } from "@ant-design/icons";
import { useScrollEffects } from "./hooks/useScrollEffects";
import { useDescBlock } from "./hooks/useDescBlock";
import "../../styles/index.scss";

type ChatProps = {
  isArchived: boolean;
  initErr: string | null;
  parentId?: string;
  contractNumber?: number;
  conversationId: string | undefined;
  mineNew: IMessage | null;
  setConversationId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setInitErr: React.Dispatch<React.SetStateAction<string | null>>;
  setInitData?: React.Dispatch<React.SetStateAction<any>>;
  onMsgSent: () => void;
};

export const Chat = ({
  isArchived,
  initErr,
  parentId,
  contractNumber,
  conversationId,
  mineNew,
  setConversationId,
  setInitErr,
  setInitData,
  onMsgSent,
}: ChatProps) => {
  const scrollContainer = useRef<HTMLDivElement>(null);

  const [canLoadMore, setCanLoadMore] = useState(true);

  const { unreads } = usePollUnreads(conversationId, mineNew, onMsgSent);

  const { data, isLoading, setData } = useFetchMessages(
    parentId,
    contractNumber,
    unreads,
    setConversationId,
    setCanLoadMore,
    setInitErr,
    setInitData
  );

  const { anchor } = useScrollEffects(isLoading, scrollContainer, data);

  const desc = useDescBlock(isArchived, initErr, data);

  const { isLoading: isLoadingMore, setRequest } = useLazyLoad(
    conversationId,
    scrollContainer,
    setData,
    setCanLoadMore
  );

  useOnScrolledToTop(
    isLoading,
    isLoadingMore,
    canLoadMore,
    data.length,
    scrollContainer,
    setRequest
  );

  return (
    <Row className="chat-body h-100 w-100" ref={scrollContainer}>
      <Row className="w-100 chat">
        {!isLoading ? (
          <>
            {data.length === 0 || !conversationId || initErr ? (
              <EmptyList data={desc} />
            ) : (
              <>
                {isLoadingMore && (
                  <Row className="w-100 justify-space-around">
                    <Spin indicator={<LoadingOutlined spin />} />
                  </Row>
                )}
                <List
                  conversationId={conversationId}
                  messages={data}
                  anchor={anchor}
                  isArchived={isArchived}
                />
              </>
            )}
          </>
        ) : (
          <Row className="w-100 justify-center pt-10 mt-10 chat-loader">
            <Spin size="large" />
          </Row>
        )}
      </Row>
    </Row>
  );
};
